// Reactive CSS media-query hook. Used by the Employee Portal to render two
// genuinely different trees by breakpoint (mobile Maeve-First + anchored stack
// vs. the desktop section nav) rather than CSS show/hide — the two trees can't
// share anchor IDs, so the choice has to happen in JS, not CSS.
//
// SPA-only (no SSR): `window` is always present at render, so the initial state
// reads `matchMedia` synchronously and there is no first-paint flash.

import { useEffect, useState } from 'react';

// Mirrors Tailwind's default `md` breakpoint (768px — unmodified in this
// project's `@theme` block, which only defines colour/font/radius/shadow
// tokens). Single source of truth for any JS-side code that needs to make
// the SAME mobile/desktop split some other surface expresses via
// `md:hidden`/`md:block` (e.g. `ManagerDashboard.tsx`) — a raw inline
// literal here would silently drift from that CSS threshold if the Tailwind
// breakpoint were ever customised.
export const TAILWIND_MD_MAX_WIDTH_QUERY = '(max-width: 767px)';

// Tailwind's default `lg` (1024px). The Employee portal splits here rather
// than at `md`: its dark section rail only renders at lg+, so below that a
// tablet gets the bottom-nav app shell instead of a nav-less gap. Manager
// splits at `md` because its chrome does.
export const TAILWIND_LG_MAX_WIDTH_QUERY = '(max-width: 1023px)';

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false,
  );

  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}
